import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import Chip from '@material-ui/core/Chip';
import Typography from '@material-ui/core/Typography';
import { FormattedMessage } from 'react-intl';
import moment from 'logic/moment';
import FormatPrice from 'components/shared/FormatPrice';
import { orderHasTax, orderHasBooking } from './Receipt';

const types = {
  PAYIN: 'info',
  PAYOUT: 'success',
  REFUND: 'success',
  PAYMENT: 'error',
  FEE: 'error',
};

export const types_translations = {
  PAYIN: <FormattedMessage id="order.payin.title" defaultMessage="Rent payins" />,
  PAYOUT: <FormattedMessage id="order.payout.title" defaultMessage="Rent payouts" />,
  REFUND: <FormattedMessage id="order.refund.title" defaultMessage="Rent refunds" />,
  PAYMENT: (
    <FormattedMessage
      id="order.payment.title"
      defaultMessage="Other payments (advertisement + extras)"
    />
  ),
  FEE: <FormattedMessage id="order.fee.title" defaultMessage="Rent fees" />,
};

const styles = makeStyles(() => ({
  success: {
    backgroundColor: 'rgb(30, 70, 32)',
    color: 'white',
  },
  error: {
    backgroundColor: 'rgb(66, 25, 24)',
    color: 'white',
  },
  info: {
    backgroundColor: 'rgb(13, 60, 97)',
  },
  desc: {
    minWidth: 200,
    display: 'inline-block',
  },
  hr: {
    borderTop: '1px solid #d2d2d2',
  },
}));

const SummaryData = ({ title, totalAmount, totalTax }) => {
  const classes = styles();

  if (totalAmount === 0) {
    return null;
  }

  return (
    <Box boxShadow={3} p={2} my={2} bgcolor="primary.light" color="white">
      <Typography>{title}</Typography>
      <Typography paragraph variant="subtitle2">
        <span className={classes.desc}>
          <FormattedMessage id="Total" defaultMessage="Total" />
        </span>
        <FormatPrice price={totalAmount} minimumFractionDigits={2} />
      </Typography>
      <Typography>
        <span className={classes.desc}>
          <FormattedMessage id="Tax" defaultMessage="Tax" />
          (25%)
        </span>
        <FormatPrice price={totalTax} minimumFractionDigits={2} />
      </Typography>
      <Typography>
        <span className={classes.desc}>
          <FormattedMessage id="Netto" defaultMessage="Net" />
        </span>
        <FormatPrice price={totalAmount - totalTax} minimumFractionDigits={2} />
      </Typography>
    </Box>
  );
};

SummaryData.propTypes = {
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.node]),
  totalAmount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  totalTax: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export const Summary = ({ orders }) => {
  const tax = orders.reduce(
    (acc, order) => {
      if (orderHasTax(order)) {
        acc[order.type] += order.amount - order.amount / 1.25;
      }

      return acc;
    },
    {
      PAYOUT: 0,
      PAYIN: 0,
      REFUND: 0,
      FEE: 0,
      PAYMENT: 0,
    },
  );
  const amount = orders.reduce(
    (acc, order) => {
      acc[order.type] += order.amount;

      return acc;
    },
    {
      PAYOUT: 0,
      PAYIN: 0,
      REFUND: 0,
      FEE: 0,
      PAYMENT: 0,
    },
  );

  return (
    <>
      <SummaryData
        title={types_translations.PAYIN}
        totalAmount={amount.PAYIN}
        totalTax={tax.PAYIN}
      />
      <SummaryData
        title={types_translations.PAYOUT}
        totalAmount={amount.PAYOUT}
        totalTax={tax.PAYOUT}
      />
      <SummaryData title={types_translations.FEE} totalAmount={amount.FEE} totalTax={tax.FEE} />
      <SummaryData
        title={types_translations.REFUND}
        totalAmount={amount.REFUND}
        totalTax={tax.REFUND}
      />
      <SummaryData
        title={types_translations.PAYMENT}
        totalAmount={amount.PAYMENT}
        totalTax={tax.PAYMENT}
      />
    </>
  );
};

Summary.propTypes = {
  orders: PropTypes.arrayOf(PropTypes.shape({})),
};

const Info = (props) => {
  const { order } = props;
  const classes = styles();

  const hasBooking = orderHasBooking(order);
  const hasTax = orderHasTax(order);

  const amount = order.amount || 0;

  return (
    <Box p={2}>
      <Grid container spacing={2}>
        <Grid item xs={4}>
          <Typography>#{order.id}</Typography>
        </Grid>
        <Grid item xs={8}>
          <Box textAlign="right">
            <Chip
              label={
                <FormattedMessage
                  id={`order.type.${order.type.toLowerCase()}`}
                  defaultMessage={order.type}
                />
              }
              color="primary"
              size="small"
              className={classes[types[order.type]]}
            />
          </Box>
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography>
            <span className={classes.desc}>
              {hasBooking && <FormattedMessage id="Booking" defaultMessage="Booking" />}
              {!hasBooking && <FormattedMessage id="Cottage" defaultMessage="Cottage" />}
            </span>
            #{hasBooking ? order.booking_id : order.cottage_id}
          </Typography>
          {order.paid_at && (
            <Typography paragraph>
              <span className={classes.desc}>
                <FormattedMessage id="Date" defaultMessage="Date" />
              </span>
              {moment(order.paid_at).format('LLL')}
            </Typography>
          )}
        </Grid>
      </Grid>
      <hr className={classes.hr} />
      <Grid container spacing={2}>
        <Grid item xs={8}>
          <Typography paragraph variant="subtitle2">
            <span className={classes.desc}>
              <FormattedMessage id="Total" defaultMessage="Total" />
            </span>
            <FormatPrice price={amount} minimumFractionDigits={2} />
          </Typography>
          {hasTax && (
            <>
              <Typography>
                <span className={classes.desc}>
                  <FormattedMessage id="Tax" defaultMessage="Tax" />
                  (25%)
                </span>
                <FormatPrice price={amount - amount / 1.25} minimumFractionDigits={2} />
              </Typography>
              <Typography>
                <span className={classes.desc}>
                  <FormattedMessage id="Netto" defaultMessage="Net" />
                </span>
                <FormatPrice price={amount - (amount - amount / 1.25)} minimumFractionDigits={2} />
              </Typography>
            </>
          )}
        </Grid>
        <Grid item xs={4}>
          <Box textAlign="right">
            <Button size="small" color="primary" component={Link} to={`/orders/${order.id}`}>
              <FormattedMessage id="Open receipt" defaultMessage="Show receipt" />
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

Info.propTypes = {
  order: PropTypes.shape({
    id: PropTypes.number,
    created_at: PropTypes.string,
    amount: PropTypes.number,
    paid_at: PropTypes.string,
    type: PropTypes.string,
    products: PropTypes.arrayOf(PropTypes.shape({})),
    booking_id: PropTypes.number,
    cottage_id: PropTypes.number,
  }).isRequired,
};

export default Info;
